/**
 * Session-scoped flags that let the app distinguish an *intentional* sign-out
 * from an *unexpected* auth drop (token/refresh failure, admin suspend, password
 * change elsewhere).
 *
 * Flow:
 *   - logoutFlow.signOutAndRedirectToLogin() calls markIntentionalLogout()
 *     right before signing out.
 *   - AuthContext, when it sees the user become null, calls
 *     consumeIntentionalLogout(). If it was NOT an intentional logout AND a user
 *     was previously signed in, it calls markSessionExpired().
 *   - PrivateRoute consumes the expired flag when redirecting to /login and adds
 *     ?reason=session-expired so LoginPage can show a notice.
 *
 * sessionStorage (not localStorage) so the signal is naturally scoped to the tab
 * and cleared when the tab closes.
 */

const INTENTIONAL_LOGOUT_KEY = "intentionalLogout";
const SESSION_EXPIRED_KEY = "authSessionExpired";

function safeStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    // Accessing sessionStorage can throw in some privacy modes.
    return null;
  }
}

function setFlag(key) {
  const store = safeStorage();
  if (store) store.setItem(key, "1");
}

/** Read a flag and clear it — returns true only on the first read after it was set. */
function consumeFlag(key) {
  const store = safeStorage();
  if (!store) return false;
  const present = store.getItem(key) === "1";
  if (present) store.removeItem(key);
  return present;
}

export function markIntentionalLogout() {
  setFlag(INTENTIONAL_LOGOUT_KEY);
}

export function consumeIntentionalLogout() {
  return consumeFlag(INTENTIONAL_LOGOUT_KEY);
}

export function markSessionExpired() {
  setFlag(SESSION_EXPIRED_KEY);
}

/** Read the expired flag WITHOUT clearing it — safe to call during render. */
export function peekSessionExpired() {
  const store = safeStorage();
  return store ? store.getItem(SESSION_EXPIRED_KEY) === "1" : false;
}

export function consumeSessionExpired() {
  return consumeFlag(SESSION_EXPIRED_KEY);
}
